import type { Tenant } from './api';

export type CollectionState = { status: string; checkedAt: string | null; lastAttemptAt: string | null; failureKind: string | null; truncated: boolean };
export type SettingsAccount = {
  accountId: string; name: string; registeredRegions: string[]; connectionStatus: string;
  serviceRegions: Record<'health' | 'securityHub' | 'waf' | 'backup', string[] | 'ALL'> & { inspector?: string[] | 'ALL' };
  connectionMethod: string; organizationRole: string; configuredAt: string | null;
  services: Record<'health' | 'securityHub' | 'waf' | 'backup', CollectionState> & { inspector?: CollectionState };
};
export type TenantSettings = {
  fetchedAt: string; tenant: Tenant & { status: string };
  consistency: { status: string; fields: string[]; contractRevision: number | null; tenantRevision: number | null };
  collection: { intervalMinutes: number; scheduleState: string; settingSource: string;
    limits: { items: number; pages: number }; services: string[];
    health: { scope: string; method: string; apiRegion: string; eventRegions: string; statuses: string[];
      closedWindowDays: number; organizationCollection: string; eventBridgeCollection: string } };
  accounts: SettingsAccount[];
};
export function settingsBoundary(tenant: Tenant) {
  return JSON.stringify([tenant.tenantId, tenant.contractId, tenant.customerId, tenant.dataGeneration, tenant.authorizationGeneration, tenant.role]);
}
export function settingsMatchTenant(settings: TenantSettings, tenant: Tenant) {
  return settingsBoundary(settings.tenant) === settingsBoundary(tenant);
}
export function settingsCollectionStatus(state?: CollectionState) {
  const status = typeof state?.status === 'string' ? state.status.toLowerCase() : undefined;
  if (!state || status === 'not_collected') return { label: '未取得', tone: 'muted' };
  if (status === 'error') return { label: ({ SUBSCRIPTION_REQUIRED: '利用条件不足', ACCESS_DENIED: '権限不足', SERVICE_NOT_ENABLED: '未有効化' } as Record<string, string>)[state.failureKind ?? ''] ?? '取得失敗', tone: 'danger' };
  if (state.truncated) return { label: '一部取得', tone: 'warning' };
  if (status === 'inactive') return { label: '未利用 / 無効', tone: 'muted' };
  if (!state.checkedAt || !Number.isFinite(Date.parse(state.checkedAt))) return { label: '未取得', tone: 'muted' };
  if (status === 'warning') return { label: '注意', tone: 'warning' };
  if (['active', 'success', 'ok'].includes(status!)) return { label: '取得成功', tone: 'good' };
  return { label: '状態不明', tone: 'warning' };
}

/** Validate wire data before rendering; old or malformed APIs must show an error, not crash React. */
export function isTenantSettings(value: unknown): value is TenantSettings {
  const object = (v: unknown): Record<string, unknown> | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
  const text = (v: unknown) => typeof v === 'string' && v.length > 0;
  const strings = (v: unknown): v is string[] => Array.isArray(v) && v.every(text);
  const timestamp = (v: unknown) => typeof v === 'string' && Number.isFinite(Date.parse(v));
  const optionalTime = (v: unknown) => v === null || timestamp(v);
  const positive = (v: unknown) => Number.isSafeInteger(v) && Number(v) > 0;
  const nullableRevision = (v: unknown) => v === null || positive(v);
  const root = object(value), tenant = object(root?.tenant), consistency = object(root?.consistency), collection = object(root?.collection);
  const health = object(collection?.health), limits = object(collection?.limits);
  if (!root || !tenant || !consistency || !collection || !health || !limits || !timestamp(root.fetchedAt)) return false;
  if (!['tenantId', 'contractId', 'customerId', 'displayName', 'role', 'status'].every(key => text(tenant[key]))
    || !['viewer', 'operator'].includes(String(tenant.role))
    || !(text(tenant.dataGeneration) || positive(tenant.dataGeneration))
    || !(tenant.authorizationGeneration === undefined || text(tenant.authorizationGeneration))) return false;
  if (!text(consistency.status) || !strings(consistency.fields) || !nullableRevision(consistency.contractRevision) || !nullableRevision(consistency.tenantRevision)) return false;
  if (!positive(collection.intervalMinutes) || !text(collection.scheduleState) || !text(collection.settingSource)
    || !strings(collection.services) || !positive(limits.items) || !positive(limits.pages)) return false;
  if (!['scope', 'method', 'apiRegion', 'eventRegions', 'organizationCollection', 'eventBridgeCollection'].every(key => text(health[key]))
    || !strings(health.statuses) || !positive(health.closedWindowDays)) return false;
  if (!Array.isArray(root.accounts)) return false;
  const ids = new Set<string>();
  for (const raw of root.accounts) {
    const account = object(raw), states = object(account?.services), regions = object(account?.serviceRegions);
    if (!account || !states || !regions || !/^\d{12}$/.test(String(account.accountId)) || ids.has(String(account.accountId))) return false;
    ids.add(String(account.accountId));
    if (!['name', 'connectionStatus', 'connectionMethod', 'organizationRole'].every(key => text(account[key]))
      || !strings(account.registeredRegions) || !optionalTime(account.configuredAt)) return false;
    for (const key of ['health', 'securityHub', 'waf', 'backup', ...(states.inspector === undefined && regions.inspector === undefined ? [] : ['inspector'])]) {
      const state = object(states[key]);
      if (!state || !text(state.status) || !optionalTime(state.checkedAt) || !optionalTime(state.lastAttemptAt)
        || !(state.failureKind === null || text(state.failureKind)) || typeof state.truncated !== 'boolean') return false;
      if (regions[key] !== 'ALL' && !strings(regions[key])) return false;
    }
  }
  return true;
}
